import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { Tabs } from '@/components/common/Tabs';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { Switch } from '@/components/forms/Switch';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { FilterBar } from '@/components/common/FilterBar';
import { LineChart } from '@/components/charts/LineChart';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { formatNumber } from '@/utils/format';
import { filterDataApi, filterDataPaged } from '@/api/services/filterData';
import { spreadMonitoringApi } from '@/api/services/spreadMonitoring';
import { useSpreadLive } from './useSpreadLive';
import { SpreadHistoricalChart } from './SpreadHistoricalChart';
import { SpreadMonitoringPermissions as P } from './permissions';
import type { SpreadLiveRow, SpreadLiveSnapshotDto } from '@/api/types/spreadMonitoring';
import type { SpreadHistoricalInput, SpreadStatsDto } from '@/api/types/spreadMonitoring';

const n2 = (v: number | null | undefined) => (v == null || Number.isNaN(Number(v)) ? 'N/A' : formatNumber(Number(v), 2));
function nowStr(offsetMin = 0): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() + offsetMin);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export function SpreadMonitoringPage() {
  const toast = useToast();
  const can = usePermission();
  const tabs = useMemo(
    () =>
      ([
        { key: 'live', title: 'Live Spread Monitoring', perm: P.liveTab },
        { key: 'historical', title: 'Historical Spread Analysis', perm: P.historicalTab },
      ] as { key: 'live' | 'historical'; title: string; perm: string }[]).filter((t) => can(t.perm)),
    [can],
  );
  const [tab, setTab] = useState<'live' | 'historical'>('live');
  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.key === tab)) setTab(tabs[0].key);
  }, [tabs, tab]);

  // ---- Live tab ----
  const [live, setLive] = useState<SpreadLiveSnapshotDto | null>(null);
  // First complete (all connectors quoted) symbol in the live table, else first symbol —
  // used to seed the Historical tab (mirrors LiveSpread firstCompleteSymbol).
  const liveSeedSymbol = useRef<string | null>(null);
  const onLive = useCallback((d: SpreadLiveSnapshotDto) => {
    setLive(d);
    const connKeys = (d.columns ?? []).map((c) => `connector_${c.id}`);
    let fallback: string | null = null;
    let complete: string | null = null;
    for (const row of d.rows ?? []) {
      if (!row.symbol) continue;
      if (fallback == null) fallback = row.symbol;
      if (complete == null && connKeys.length > 0 && connKeys.every((k) => row[k] != null)) complete = row.symbol;
    }
    liveSeedSymbol.current = complete ?? fallback;
    if (d.errorMessage) toast.error(d.errorMessage);
  }, [toast]);
  useSpreadLive({ enabled: tab === 'live', onData: onLive, onError: (m) => toast.error(m) });

  // Per-connector inactive warnings, raised once per distinct set (mirrors LiveSpread notifyInactiveConnectors).
  const lastInactiveKey = useRef('');
  useEffect(() => {
    const names = live?.inactiveConnectorNames ?? [];
    const key = names.slice().sort().join('|');
    if (key === lastInactiveKey.current) return;
    lastInactiveKey.current = key;
    for (const name of names) {
      toast.info(`There is an issue with the ${name}. The displayed data may not be accurate. Please contact the administrator.`);
    }
  }, [live, toast]);

  const liveCols: DataGridColumn<SpreadLiveRow>[] = [
    { key: 'symbol', title: 'Symbol', className: 'text-start', value: (r) => r.symbol },
    ...(live?.columns ?? []).map((c) => ({
      key: `connector_${c.id}`,
      title: c.name,
      className: 'text-end',
      value: (r: SpreadLiveRow) => (r[`connector_${c.id}`] as number) ?? null,
      cell: (r: SpreadLiveRow) => n2(r[`connector_${c.id}`] as number),
    })),
  ];

  // ---- Historical tab ----
  const connectors = useQuery({ queryKey: ['rd-filter', 'connectors'], queryFn: filterDataApi.getEnabledConnectors, staleTime: 5 * 60_000, enabled: tab === 'historical' });
  const [hist, setHist] = useState<SpreadHistoricalInput>({ symbol: '', startDate: nowStr(-30), endDate: nowStr(0), connectorId: null, spreadThreshold: null });
  const [applied, setApplied] = useState<SpreadHistoricalInput | null>(null);
  const userEditedSymbol = useRef(false);
  // Bumped per Fetch (only when the range ends today) so re-applying identical filters
  // forces a fresh request; static historical results stay cached. react-query keys by
  // value. fetchPulse gives the Fetch button a brief spinner even on a cache hit.
  const [fetchNonce, setFetchNonce] = useState(0);
  const [fetchPulse, setFetchPulse] = useState(false);

  // On entering Historical, seed the symbol from the live table once — never overwriting a user's pick.
  useEffect(() => {
    if (tab !== 'historical' || userEditedSymbol.current) return;
    const seed = liveSeedSymbol.current;
    if (seed) setHist((f) => (f.symbol ? f : { ...f, symbol: seed }));
  }, [tab]);
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');
  const [showDots, setShowDots] = useState(false);

  const histQuery = useQuery({
    queryKey: ['spread-hist', applied, fetchNonce],
    queryFn: () => spreadMonitoringApi.getHistorical(applied as SpreadHistoricalInput),
    enabled: !!applied,
  });
  const r = histQuery.data;
  useEffect(() => {
    if (!r) return;
    if (r.warningMessage) toast.info(r.warningMessage);
    if (r.failedConnectorNames?.length) toast.error(`No data from ${r.failedConnectorNames.join(', ')}`);
    if (r.unsupportedConnectorNames?.length) toast.info(`Tick history not available on ${r.unsupportedConnectorNames.join(', ')}`);
    if (r.unresolvedDigitsTickCount) toast.info(`${r.unresolvedDigitsTickCount} tick(s) skipped: unknown symbol digits`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r]);

  const fetchHist = () => {
    if (!hist.symbol) {
      toast.error('Select a symbol to analyse.');
      return;
    }
    setApplied({ ...hist });
    if (new Date(hist.endDate).toDateString() === new Date().toDateString()) setFetchNonce((n) => n + 1);
    setFetchPulse(true);
    window.setTimeout(() => setFetchPulse(false), 450);
  };

  const str = (v: string | number | null | undefined) => (v == null ? '' : String(v));
  const histFields = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date Range</label>
          <DateRangePicker mode="range" enableTime maxDate={nowStr(0)} value={{ start: hist.startDate, end: hist.endDate }} onChange={(v) => setHist((f) => ({ ...f, startDate: v.start, endDate: v.end }))} />
        </div>
      ),
      pinned: true,
      width: 270,
    },
    <Select2 key="conn" label="Connector" allowClear placeholder="All" value={str(hist.connectorId)} options={connectors.data ?? []} onChange={(v) => setHist((f) => ({ ...f, connectorId: v ? Number(v) : null }))} />,
    <div key="sym">
      <label className="form-label">Symbol</label>
      <Select2Async placeholder="Select symbol" value={hist.symbol || null} fetch={filterDataPaged.instruments} onChange={(v) => { userEditedSymbol.current = true; setHist((f) => ({ ...f, symbol: v || '' })); }} />
    </div>,
    <div key="th">
      <label className="form-label">Spread Threshold</label>
      <input className="form-control" inputMode="decimal" value={hist.spreadThreshold ?? ''} onChange={(e) => setHist((f) => ({ ...f, spreadThreshold: e.target.value ? Number(e.target.value) : null }))} />
    </div>,
  ];

  const statsCols: DataGridColumn<SpreadStatsDto>[] = [
    { key: 'connectorName', title: 'Connector', className: 'text-start', value: (s) => s.connectorName },
    { key: 'averageSpread', title: 'Avg Spread', className: 'text-end', value: (s) => s.averageSpread ?? null, cell: (s) => n2(s.averageSpread) },
    { key: 'minimumSpread', title: 'Min Spread', className: 'text-end', value: (s) => s.minimumSpread ?? null, cell: (s) => n2(s.minimumSpread) },
    { key: 'maximumSpread', title: 'Max Spread', className: 'text-end', value: (s) => s.maximumSpread ?? null, cell: (s) => n2(s.maximumSpread) },
  ];

  return (
    <div className="spread-monitoring-page">
      <PageHeader title="Spread Monitoring" breadcrumb={['Home', 'Spread Monitoring']} />

      <div className="card custom-card mb-2">
        <div className="card-header p-0"><Tabs variant="underline" tabs={tabs.map((t) => ({ key: t.key, title: t.title }))} active={tab} onChange={(k) => setTab(k as 'live' | 'historical')} /></div>
        <div className="card-body">
          {tab === 'live' && (
            <DataGrid columns={liveCols} data={live?.rows ?? []} rowKey={(r) => r.symbol} pageSize={5} card={false} includeExcel excelFileName="Live Spread" columnVisibilityToggle={false} emptyMessage="Waiting for live spreads from connected feeds." />
          )}

          {tab === 'historical' && (
            <>
              <FilterBar
                card={false}
                fields={histFields}
                actions={
                  <button type="button" className="btn btn-primary btn-sm" onClick={fetchHist} disabled={histQuery.isFetching || fetchPulse}>
                    {histQuery.isFetching || fetchPulse ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}Fetch
                  </button>
                }
              />
              <div className="border-top pt-2 mt-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Spread</h6>
                  <div className="d-flex align-items-center gap-3">
                    <div style={{ minWidth: 140 }}>
                      <Select2 searchable={false} allowClear={false} value={chartType} options={[{ value: 'line', label: 'Line Chart' }, { value: 'bar', label: 'Bar Chart' }]} onChange={(v) => setChartType((v as 'line' | 'bar') || 'line')} />
                    </div>
                    <Switch label="Show Dots" checked={showDots} onChange={setShowDots} />
                  </div>
                </div>
                {r && r.datasets?.length ? (
                  <SpreadHistoricalChart result={r} type={chartType} showDots={showDots} threshold={applied?.spreadThreshold ?? null} />
                ) : (
                  <LineChart labels={[]} series={[]} height={340} emptyHint={histQuery.isFetching ? 'Loading…' : 'Pick a symbol and Fetch to analyse spread.'} />
                )}
              </div>
              <div className="border-top pt-2 mt-2">
                <DataGrid columns={statsCols} data={r?.statistics ?? []} rowKey={(s) => String(s.connectorId)} pageSize={5} card={false} includeExcel excelFileName="Historical Spread" columnVisibilityToggle={false} emptyMessage="No statistics." />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
